/* ==========================================================================
   POS Billing screen

   A till-style companion to the Sales Invoice modal. Both write the same
   `InvSales` record with salesTransType 2, so a POS bill and a web invoice land
   in the same ledger and the same reports; only the entry-number series differs
   ("POS…" rather than "SI…") so a bill can be traced back to the terminal it
   was rung up on.

   Layout: fixed to one viewport. The bill lines scroll inside their card and
   the payment rail scrolls inside itself; nothing else moves.
   ========================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import {
  ShoppingCart,
  Sun,
  User,
  Settings,
  LogOut,
  Plus,
  Minus,
  Trash2,
  Search,
  Grid,
  Package,
  Tag,
  Percent,
  Circle,
  RotateCcw,
  Pause,
  Clock,
  FileText,
  CreditCard,
  DollarSign,
  Smartphone,
  Save,
  Printer,
  MoreVertical,
  Delete,
  Home,
  Phone,
  UserCheck,
  Hash,
  Truck,
  HelpCircle,
  X,
  CornerUpLeft,
} from "feather-icons-react/build/IconComponents";

import POSField from "./POSField";
import {
  ItemLookupDialog,
  StockPriceCheckDialog,
  ManualItemDialog,
  RecentBillsDialog,
  HeldBillsDialog,
  NotesDialog,
  ShortcutsDialog,
} from "./POSDialogs";
import {
  num,
  money,
  emptyLine,
  lineTotals,
  billTotals,
  isRealLine,
  nextEntryNumber,
  shiftLabel,
  readHeldBills,
  writeHeldBills,
  buildReceiptHtml,
  printHtml,
} from "./posHelpers";

import { getWareHouseList } from "../../../services/MasterApiServices";
import { addEditSales, getSalesByTransType } from "../../../services/SalesApiServices";
import { getPrintSettingsByBranch } from "../../../services/AdminApiServices";
import {
  fetchProduct,
  fetchCustomer,
  fetchTax,
  fetchUnit,
  fetchSettings,
} from "../../../redux/userSetting";
import { fetchBranchSettings } from "../../../redux/settingbyBranch";
import { fetchUserByBranch } from "../../../redux/userServices";
import { InvSales } from "../../../core/json/invSales";
import { FinancialYear } from "../../../core/json/FinancialYear";
import CustomerModal from "../../master/modal/CustomerModal";

import "../../../style/css/pos-screen.css";

const PAY_MODES = [
  { value: 2, key: "upi", label: "UPI", icon: <Smartphone />, className: "pos-paymode--upi" },
  { value: 0, key: "cash", label: "Cash", icon: <DollarSign />, className: "pos-paymode--cash" },
  { value: 1, key: "credit", label: "Credit", icon: <FileText />, className: "pos-paymode--credit" },
  { value: 3, key: "card", label: "Card", icon: <CreditCard />, className: "pos-paymode--card" },
];

const ZERO_PAYMENTS = { cash: "", card: "", upi: "", credit: "" };

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 11000 }),
  menu: (base) => ({ ...base, zIndex: 11000 }),
};

const POSBillingScreen = ({ onExit }) => {
  const dispatch = useDispatch();

  /* ---------------- redux ---------------- */
  const { branch } = useSelector((state) => state.branchlist);
  const { userId, userName } = useSelector((state) => state.userauth);
  const { productList, customerList, taxList, unitList, settingList } = useSelector(
    (state) => state.usersetting
  );
  const { branchsettings } = useSelector((state) => state.settings);
  const { UserListByBranch } = useSelector((state) => state.userservice);
  const { companyInfolist } = useSelector((state) => state.companyInfo);

  /* ---------------- bill state ---------------- */
  const [lines, setLines] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [entryNumber, setEntryNumber] = useState("");
  const [invDate, setInvDate] = useState(dayjs());
  const [customer, setCustomer] = useState(null);
  const [mobile, setMobile] = useState("");
  const [warehouse, setWarehouse] = useState(null);
  const [salesman, setSalesman] = useState(null);
  const [refNo, setRefNo] = useState("");
  const [terms, setTerms] = useState("");
  const [dispatchTo, setDispatchTo] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(dayjs());
  const [notes, setNotes] = useState("");
  const [extraCharge, setExtraCharge] = useState(0);
  const [billDiscount, setBillDiscount] = useState(0);
  const [roundOff, setRoundOff] = useState(0);

  /* ---------------- payment state ---------------- */
  const [paid, setPaid] = useState("");
  const [payMode, setPayMode] = useState(0);
  const [splitPay, setSplitPay] = useState(false);
  const [payments, setPayments] = useState(ZERO_PAYMENTS);

  /* ---------------- ui state ---------------- */
  const [scan, setScan] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState("paid");
  const [entry, setEntry] = useState("");
  const [dialog, setDialog] = useState(null); // lookup | quick | stock | manual | recent | held | notes | keys
  const [lookupTerm, setLookupTerm] = useState(""); // seeds the look-up from the scan bar
  const [popover, setPopover] = useState(null); // { name, top, left }
  const [customerModal, setCustomerModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [clock, setClock] = useState(dayjs());
  // Off by default: SAVE saves, SAVE & PRINT prints. Turn it on for a terminal
  // that should spit a receipt out either way.
  const [printOnSave, setPrintOnSave] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [printSettings, setPrintSettings] = useState(null);
  const [recentBills, setRecentBills] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [heldBills, setHeldBills] = useState([]);

  const scanRef = useRef(null);
  const paidRef = useRef(null);

  /* ---------------- derived settings ---------------- */

  // One decimal setting drives the whole screen, read the same way the invoice
  // modal reads it.
  const decimals = useMemo(() => {
    const match = settingList?.currencyFormat?.toString()?.match(/\.0+/);
    return match ? match[0].length - 1 : 2;
  }, [settingList]);

  // Tax columns are hidden only when the branch explicitly disables tax in
  // sales; an unloaded setting must not silently drop tax from a bill.
  const showTax = branchsettings?.disableTaxInSale !== true;

  const fmt = useCallback((value) => money(value, decimals), [decimals]);

  const taxOf = useCallback(
    (taxGuid) => num((taxList || []).find((tax) => tax.guid === taxGuid)?.percentage),
    [taxList]
  );

  /* ---------------- option lists ---------------- */

  const productOptions = useMemo(
    () =>
      (productList || []).map((product) => ({
        value: product.guid,
        label: product.name,
        code: product.code,
        barcode: product.barcode,
        product,
      })),
    [productList]
  );

  const customerOptions = useMemo(
    () => (customerList || []).map((item) => ({ value: item.guid, label: item.name, customer: item })),
    [customerList]
  );

  const warehouseOptions = useMemo(
    () => (warehouses || []).map((item) => ({ value: item.guid, label: item.name, warehouse: item })),
    [warehouses]
  );

  const salesmanOptions = useMemo(
    () => (UserListByBranch || []).map((user) => ({ value: user.guid, label: user.name })),
    [UserListByBranch]
  );

  const productFilter = (option, rawInput) => {
    const needle = `${rawInput ?? ""}`.trim().toLowerCase();
    if (!needle) return true;
    const { label, code, barcode } = option.data || {};
    return (
      `${label ?? ""}`.toLowerCase().includes(needle) ||
      `${code ?? ""}`.toLowerCase().includes(needle) ||
      `${barcode ?? ""}`.toLowerCase().includes(needle)
    );
  };

  /* ---------------- totals ---------------- */

  const totals = useMemo(
    () => billTotals(lines, { extraCharge, billDiscount, roundOff }),
    [lines, extraCharge, billDiscount, roundOff]
  );

  // "VAT 5%" when every taxed line shares a rate, plain "Tax" when they differ.
  const taxLabel = useMemo(() => {
    const rates = new Set(lines.filter((line) => num(line.taxPercent) > 0).map((line) => num(line.taxPercent)));
    if (rates.size === 1) return `VAT ${[...rates][0]}%`;
    return "Tax";
  }, [lines]);

  const splitTotal = useMemo(
    () => Object.values(payments).reduce((total, value) => total + num(value), 0),
    [payments]
  );

  const isCreditOnly = !splitPay && payMode === 1;
  const paidTotal = splitPay ? splitTotal : isCreditOnly ? totals.net : num(paid);
  const change = paidTotal - totals.net;
  const activeLine = lines.find((line) => line.key === activeKey) || null;

  // Tender suggestions built from the amount actually due, so the chips are
  // useful for any bill rather than a fixed ladder.
  const quickCash = useMemo(() => {
    const due = totals.net;
    if (due <= 0) return [];
    const ceilTo = (step) => Math.ceil(due / step) * step;
    const candidates = [Math.round(due * 100) / 100, ceilTo(10), ceilTo(50), ceilTo(100), ceilTo(500), 1000];
    return [...new Set(candidates.filter((value) => value >= due))].sort((a, b) => a - b).slice(0, 6);
  }, [totals.net]);

  /* ---------------- data loading ---------------- */

  useEffect(() => {
    if (!branch?.guid) return;
    dispatch(fetchProduct(branch));
    dispatch(fetchCustomer(branch));
    dispatch(fetchTax());
    dispatch(fetchUnit());
    dispatch(fetchSettings(branch));
    dispatch(fetchBranchSettings(branch.guid));
    dispatch(fetchUserByBranch(branch));
  }, [dispatch, branch]);

  useEffect(() => {
    if (!branch?.guid) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const response = await getWareHouseList();
        if (cancelled) return;
        const list = Array.isArray(response) ? response.filter((item) => item.branchID === branch.guid) : [];
        setWarehouses(list);
      } catch (error) {
        console.error("Failed to fetch warehouses", error);
      }

      try {
        const settings = await getPrintSettingsByBranch(branch.guid);
        if (!cancelled) setPrintSettings(settings);
      } catch (error) {
        console.error("Failed to fetch print settings", error);
      }
    })();

    setHeldBills(readHeldBills(branch.guid));
    return () => {
      cancelled = true;
    };
  }, [branch]);

  // Default warehouse from branch settings, once both have arrived.
  useEffect(() => {
    if (warehouse || !warehouses.length) return;
    const preferred = warehouses.find((item) => item.guid === branchsettings?.defaultWareHouseID);
    const chosen = preferred || warehouses[0];
    if (chosen) setWarehouse({ value: chosen.guid, label: chosen.name, warehouse: chosen });
  }, [warehouses, branchsettings, warehouse]);

  const loadEntryNumber = useCallback(async () => {
    if (!branch?.guid) return;
    try {
      const response = await getSalesByTransType(branch, 2);
      setEntryNumber(nextEntryNumber(response?.data, "POS"));
    } catch (error) {
      console.error("Failed to read the bill series", error);
      setEntryNumber("POS1");
    }
  }, [branch]);

  useEffect(() => {
    loadEntryNumber();
  }, [loadEntryNumber]);

  // Tax rates arrive after the first render. Re-resolve any line that was rung
  // up before the list landed, so a fast first scan is never quietly untaxed.
  useEffect(() => {
    if (!taxList?.length) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (!line.taxGuid) return line;
        const percent = taxOf(line.taxGuid);
        if (percent === num(line.taxPercent)) return line;
        changed = true;
        return { ...line, taxPercent: percent };
      });
      return changed ? next : prev;
    });
  }, [taxList, taxOf]);

  // Wall clock in the top bar, and the online pill.
  useEffect(() => {
    const timer = setInterval(() => setClock(dayjs()), 30000);
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  /* ---------------- line editing ---------------- */

  const focusScan = useCallback(() => {
    setTimeout(() => scanRef.current?.focus(), 0);
  }, []);

  const addProduct = useCallback(
    (product, quantity = 1) => {
      if (!product) return;
      setLines((prev) => {
        // Scanning the same item twice is one line with a bigger quantity -
        // that is what the customer sees on the receipt.
        const existing = prev.findIndex((line) => line.product === product.guid && !line.manual);
        if (existing !== -1) {
          const next = [...prev];
          next[existing] = {
            ...next[existing],
            quantity: num(next[existing].quantity) + quantity,
          };
          setActiveKey(next[existing].key);
          return next;
        }

        const line = {
          ...emptyLine(),
          product: product.guid,
          name: product.name,
          code: product.code || "",
          barcode: product.barcode || "",
          unit: product?.unit?.guid ?? product?.unit ?? null,
          unitName: product?.unit?.name ?? "",
          taxGuid: product?.taxID ?? null,
          taxPercent: taxOf(product?.taxID),
          quantity,
          price: num(product?.retailRate ?? product?.basePrice ?? product?.cost),
          mrp: num(product?.mrp),
        };
        setActiveKey(line.key);
        return [...prev, line];
      });
    },
    [taxOf]
  );

  const updateLine = useCallback((key, patch) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }, []);

  const removeLine = useCallback(
    (key) => {
      setLines((prev) => prev.filter((line) => line.key !== key));
      setActiveKey((prev) => (prev === key ? null : prev));
    },
    []
  );

  const stepQuantity = (line, delta) => {
    const next = Math.max(0, num(line.quantity) + delta);
    if (next === 0) removeLine(line.key);
    else updateLine(line.key, { quantity: next });
  };

  /* ---------------- scanning ---------------- */

  const handleScan = (raw) => {
    const scanned = `${raw ?? ""}`.trim();
    if (!scanned) return;

    // "3*BARCODE" or "3xBARCODE" rings three of them, the usual till shorthand.
    const shorthand = scanned.match(/^(\d+(?:\.\d+)?)\s*[*x]\s*(.+)$/i);
    const quantity = shorthand ? num(shorthand[1], 1) : 1;
    const needle = (shorthand ? shorthand[2] : scanned).trim().toLowerCase();

    // A scanner sends an exact code, so that wins outright.
    const exact = (productList || []).find(
      (product) =>
        `${product?.barcode ?? ""}`.trim().toLowerCase() === needle ||
        `${product?.code ?? ""}`.trim().toLowerCase() === needle
    );

    if (exact) {
      addProduct(exact, quantity);
      setScan("");
      return;
    }

    // Typed text falls back to a name search: one hit rings it up, several open
    // the look-up already filtered, none is an error.
    const partial = (productList || []).filter(
      (product) =>
        `${product?.name ?? ""}`.toLowerCase().includes(needle) ||
        `${product?.code ?? ""}`.toLowerCase().includes(needle) ||
        `${product?.barcode ?? ""}`.toLowerCase().includes(needle)
    );

    if (partial.length === 1) {
      addProduct(partial[0], quantity);
      setScan("");
      return;
    }

    if (partial.length > 1) {
      setLookupTerm(needle);
      setDialog("lookup");
      setScan("");
      return;
    }

    toast.error(`No item found for “${needle}”`);
  };

  /* ---------------- keypad ---------------- */

  const applyKeypadValue = useCallback(
    (raw) => {
      const value = raw === "" ? "" : num(raw);
      if (keypadTarget === "paid") {
        setPaid(raw);
        return;
      }
      if (!activeLine) return;
      if (keypadTarget === "qty") updateLine(activeLine.key, { quantity: value === "" ? 0 : value });
      if (keypadTarget === "price") updateLine(activeLine.key, { price: value === "" ? 0 : value });
    },
    [keypadTarget, activeLine, updateLine]
  );

  const pressKey = (token) => {
    if (token === "clear") {
      setEntry("");
      applyKeypadValue("");
      return;
    }

    if (token === "back") {
      const next = entry.slice(0, -1);
      setEntry(next);
      applyKeypadValue(next);
      return;
    }

    if (token === "+" || token === "-") {
      const current =
        keypadTarget === "paid"
          ? num(paid)
          : keypadTarget === "qty"
          ? num(activeLine?.quantity)
          : num(activeLine?.price);
      const next = Math.max(0, current + (token === "+" ? 1 : -1));
      setEntry(`${next}`);
      applyKeypadValue(`${next}`);
      return;
    }

    if (token === "." && entry.includes(".")) return;

    const next = `${entry}${token}`;
    setEntry(next);
    applyKeypadValue(next);
  };

  const switchTarget = (target) => {
    setKeypadTarget(target);
    setEntry("");
  };

  // Selecting a line hands the keypad to that line's quantity, which is what a
  // cashier reaches for next; the Paid tile takes it back on click.
  const selectLine = (key) => {
    setActiveKey(key);
    if (keypadTarget === "paid") switchTarget("qty");
    else setEntry("");
  };

  /* ---------------- bill actions ---------------- */

  const resetBill = useCallback(
    (silent = false) => {
      setLines([]);
      setActiveKey(null);
      setCustomer(null);
      setMobile("");
      setSalesman(null);
      setRefNo("");
      setTerms("");
      setDispatchTo("");
      setNotes("");
      setExtraCharge(0);
      setBillDiscount(0);
      setRoundOff(0);
      setPaid("");
      setPayments(ZERO_PAYMENTS);
      setSplitPay(false);
      setPayMode(0);
      setEntry("");
      setKeypadTarget("paid");
      setScan("");
      setInvDate(dayjs());
      loadEntryNumber();
      if (!silent) focusScan();
    },
    [loadEntryNumber, focusScan]
  );

  const holdBill = useCallback(() => {
    if (!lines.length) {
      toast.error("There is nothing to hold");
      return;
    }
    const bill = {
      id: `${Date.now()}`,
      heldAt: new Date().toISOString(),
      customerName: customer?.label || "",
      customer,
      mobile,
      salesman,
      notes,
      extraCharge,
      billDiscount,
      roundOff,
      lines,
    };
    const next = [bill, ...readHeldBills(branch?.guid)].slice(0, 50);
    writeHeldBills(branch?.guid, next);
    setHeldBills(next);
    resetBill(true);
    focusScan();
    toast.success("Bill held");
  }, [lines, customer, mobile, salesman, notes, extraCharge, billDiscount, roundOff, branch, resetBill, focusScan]);

  const recallBill = (bill) => {
    setLines(bill.lines || []);
    setCustomer(bill.customer || null);
    setMobile(bill.mobile || "");
    setSalesman(bill.salesman || null);
    setNotes(bill.notes || "");
    setExtraCharge(num(bill.extraCharge));
    setBillDiscount(num(bill.billDiscount));
    setRoundOff(num(bill.roundOff));
    setActiveKey(null);

    const next = readHeldBills(branch?.guid).filter((item) => item.id !== bill.id);
    writeHeldBills(branch?.guid, next);
    setHeldBills(next);
    setDialog(null);
    focusScan();
  };

  const deleteHeldBill = (id) => {
    const next = readHeldBills(branch?.guid).filter((item) => item.id !== id);
    writeHeldBills(branch?.guid, next);
    setHeldBills(next);
  };

  const openRecentBills = async () => {
    setDialog("recent");
    setRecentLoading(true);
    try {
      const response = await getSalesByTransType(branch, 2);
      const list = Array.isArray(response?.data) ? response.data : [];
      setRecentBills(
        [...list]
          .sort((a, b) => dayjs(b.invdate).valueOf() - dayjs(a.invdate).valueOf())
          .slice(0, 40)
      );
    } catch (error) {
      toast.error("Could not load recent bills");
    } finally {
      setRecentLoading(false);
    }
  };

  /* ---------------- printing ---------------- */

  const receiptHeader = useMemo(() => {
    const settings = printSettings?.[0] ?? {};
    return {
      companyName: settings.companyName || companyInfolist?.[0]?.name || branch?.name || "",
      header1: settings.header1 || companyInfolist?.[0]?.address1,
      header2: settings.header2 || companyInfolist?.[0]?.address2,
      header3: settings.header3,
      header4: settings.header4,
      header5: settings.header5,
      footerText1: settings.footerText1,
      footerText2: settings.footerText2,
    };
  }, [printSettings, companyInfolist, branch]);

  const printCurrentBill = useCallback(
    (billNumber) => {
      printHtml(
        buildReceiptHtml({
          header: receiptHeader,
          entryNumber: billNumber || entryNumber,
          invoiceDate: invDate.format("DD-MM-YYYY HH:mm"),
          customerName: customer?.label,
          cashier: userName || userId?.name,
          salesman: salesman?.label,
          lines: lines.filter(isRealLine),
          totals,
          payments: PAY_MODES.map((mode) => ({
            label: mode.label,
            amount: splitPay ? payments[mode.key] : payMode === mode.value ? totals.net : 0,
          })),
          paid: paidTotal,
          change: Math.max(0, change),
          notes,
          decimals,
          taxLabel,
        })
      );
    },
    [
      receiptHeader,
      entryNumber,
      invDate,
      customer,
      userName,
      userId,
      salesman,
      lines,
      totals,
      splitPay,
      payments,
      payMode,
      paidTotal,
      change,
      notes,
      decimals,
      taxLabel,
    ]
  );

  const printSavedBill = (bill) => {
    const details = bill?.invSalesDetails || [];
    const billLines = details.map((detail) => ({
      name: detail?.product?.name || detail?.description || "Item",
      quantity: num(detail?.quantity),
      price: num(detail?.price),
      discountPercent: num(detail?.discPercentage),
      taxPercent: num(detail?.taxPercentage ?? detail?.TaxPercentage),
    }));

    printHtml(
      buildReceiptHtml({
        header: receiptHeader,
        entryNumber: bill?.entrynumber,
        invoiceDate: bill?.invdate ? dayjs(bill.invdate).format("DD-MM-YYYY HH:mm") : "",
        customerName: bill?.customerName || bill?.customer?.name,
        lines: billLines,
        totals: {
          gross: num(bill?.grossTotal),
          lineDiscount: num(bill?.discountTotal) - num(bill?.discAmt),
          billDiscount: num(bill?.discAmt),
          extraCharge: num(bill?.additionalAmt),
          tax: num(bill?.taxTotal),
          roundOff: num(bill?.roundOff),
          net: num(bill?.netTotal),
          units: billLines.reduce((total, line) => total + num(line.quantity), 0),
        },
        payments: [
          { label: "Cash", amount: bill?.cash },
          { label: "Card", amount: bill?.card },
          { label: "UPI", amount: bill?.upi },
          { label: "Credit", amount: bill?.bank },
        ],
        paid: num(bill?.paid),
        change: num(bill?.change),
        decimals,
      })
    );
  };

  /* ---------------- saving ---------------- */

  const saveBill = useCallback(
    async (alsoPrint) => {
      const billLines = lines.filter(isRealLine);

      if (!billLines.length) {
        toast.error("Add at least one item before saving");
        return;
      }
      if (!branch?.guid) {
        toast.error("No branch is selected");
        return;
      }
      if (paidTotal < totals.net - 0.005) {
        toast.error(`Payment is short by ${fmt(totals.net - paidTotal)}`);
        return;
      }

      const amounts = splitPay
        ? {
            cash: num(payments.cash),
            card: num(payments.card),
            upi: num(payments.upi),
            credit: num(payments.credit),
          }
        : {
            cash: payMode === 0 ? totals.net : 0,
            credit: payMode === 1 ? totals.net : 0,
            upi: payMode === 2 ? totals.net : 0,
            card: payMode === 3 ? totals.net : 0,
          };

      // The mode that carried most of the bill is the one the record is filed
      // under, which is all `salesPaymentType` can express.
      const dominant = PAY_MODES.reduce((best, mode) =>
        amounts[mode.key] > amounts[best.key] ? mode : best
      );

      const objsales = new InvSales();
      const objfinance = new FinancialYear();

      objsales.entrynumber = entryNumber;
      objsales.branch = branch;
      objsales.invdate = invDate.format("YYYY-MM-DDTHH:mm:ss");
      objsales.deliveryDate = deliveryDate.format("YYYY-MM-DDTHH:mm:ss");
      objsales.salesTransType = 2;
      objsales.salesPaymentType = dominant.value;
      objsales.salesMode = 0;
      objsales.reftype = 0;
      objsales.refNo = refNo || "";
      objsales.currency = "00000000-0000-0000-0000-000000000000";
      objsales.customer = (customerList || []).find((item) => item.guid === customer?.value) || null;
      objsales.customerName = customer?.label || "Walk-in Customer";
      objsales.customerContactno = mobile || "";
      objsales.salesman = salesman?.value || null;
      objsales.location = warehouse?.warehouse || null;
      objsales.terms = terms || "";
      objsales.dispatch = dispatchTo || "";
      objsales.dispatchTrough = "";
      objsales.createdUser = `${userId?.id ?? ""}`;
      objsales.updatedUser = `${userId?.id ?? ""}`;

      objsales.additionalAmt = totals.extraCharge;
      objsales.discAmt = totals.billDiscount;
      objsales.roundOff = totals.roundOff;
      objsales.grossTotal = totals.gross;
      objsales.discountTotal = totals.discountTotal;
      objsales.taxTotal = totals.tax;
      objsales.netTotal = totals.net;
      objsales.paid = paidTotal;
      objsales.change = Math.max(0, change);

      objsales.cash = amounts.cash;
      objsales.card = amounts.card;
      objsales.upi = amounts.upi;
      // Credit rides in `bank`, the same column the invoice modal writes it to.
      objsales.bank = amounts.credit;

      objfinance.id = branchsettings?.defaultFinancialYearID;
      objsales.financialYear = objfinance;
      objsales.Company = companyInfolist?.[0];

      objsales.invSalesDetails = billLines.map((line) => {
        const totalsForLine = lineTotals(line);
        return {
          product: (productList || []).find((item) => item.guid === line.product) || null,
          unit: (unitList || []).find((item) => item.guid === line.unit) || null,
          itemCode: line.code || "",
          description: line.name || "",
          batch: line.batch || "",
          expiryDate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
          quantity: num(line.quantity),
          price: num(line.price),
          mrp: num(line.mrp),
          foc: num(line.foc),
          grossAmount: totalsForLine.gross,
          discPercentage: num(line.discountPercent),
          discAmt: totalsForLine.discountAmount,
          taxPercentage: num(line.taxPercent),
          taxamnt: totalsForLine.taxAmount,
          netAmt: totalsForLine.net,
          notes: line.notes || "",
        };
      });

      setSaving(true);
      try {
        const response = await addEditSales(objsales);
        if (!response) throw new Error("Empty response");

        toast.success(`Bill ${entryNumber} saved`);
        if (alsoPrint || printOnSave) printCurrentBill(entryNumber);
        resetBill(true);
        focusScan();
      } catch (error) {
        toast.error(error?.response?.data?.Message || "Could not save the bill");
        console.error("Failed to save POS bill", error);
      } finally {
        setSaving(false);
      }
    },
    [
      lines,
      branch,
      paidTotal,
      totals,
      fmt,
      splitPay,
      payments,
      payMode,
      entryNumber,
      invDate,
      deliveryDate,
      refNo,
      customerList,
      customer,
      mobile,
      salesman,
      warehouse,
      terms,
      dispatchTo,
      userId,
      change,
      branchsettings,
      companyInfolist,
      productList,
      unitList,
      printOnSave,
      printCurrentBill,
      resetBill,
      focusScan,
    ]
  );

  const confirmReset = () => {
    if (!lines.length) {
      resetBill();
      return;
    }
    Swal.fire({
      title: "Clear this bill?",
      text: "Every line on the current bill will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6d4aec",
      cancelButtonColor: "#6f6c82",
      confirmButtonText: "Clear it",
    }).then((result) => {
      if (result.isConfirmed) resetBill();
    });
  };

  /* ---------------- keyboard ---------------- */

  useEffect(() => {
    const onKeyDown = (event) => {
      const key = event.key;

      // A dialog owns the keyboard while it is open; it closes on Escape.
      if (dialog) return;

      if (key === "F1") {
        event.preventDefault();
        setDialog("keys");
      } else if (key === "F2") {
        event.preventDefault();
        switchTarget("paid");
        paidRef.current?.focus();
      } else if (key === "F3") {
        event.preventDefault();
        focusScan();
      } else if (key === "F4") {
        event.preventDefault();
        setDialog("lookup");
      } else if (key === "F6") {
        event.preventDefault();
        setDialog("quick");
      } else if (key === "F7") {
        event.preventDefault();
        holdBill();
      } else if (key === "F8") {
        event.preventDefault();
        setDialog("held");
      } else if (key === "F9") {
        event.preventDefault();
        saveBill(false);
      } else if (key === "F10") {
        event.preventDefault();
        saveBill(true);
      } else if (key === "Delete" && activeKey && document.activeElement?.tagName !== "INPUT") {
        event.preventDefault();
        removeLine(activeKey);
      } else if ((key === "ArrowDown" || key === "ArrowUp") && lines.length) {
        if (document.activeElement?.tagName === "INPUT") return;
        event.preventDefault();
        const index = lines.findIndex((line) => line.key === activeKey);
        const nextIndex =
          key === "ArrowDown"
            ? Math.min(lines.length - 1, index + 1)
            : Math.max(0, (index === -1 ? lines.length : index) - 1);
        setActiveKey(lines[nextIndex]?.key ?? null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeKey, lines, dialog, holdBill, saveBill, removeLine, focusScan]);

  useEffect(() => {
    focusScan();
  }, [focusScan]);

  /* ---------------- popovers ---------------- */

  const openPopover = (name) => (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    // Keep the panel inside the viewport whichever tile was clicked.
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - 280);
    const top = Math.max(12, rect.top - 12);
    setPopover((prev) => (prev?.name === name ? null : { name, left, top }));
  };

  const closePopover = () => setPopover(null);

  useEffect(() => {
    if (!popover) return undefined;
    const onClick = (event) => {
      if (!event.target.closest?.(".pos-pop") && !event.target.closest?.("[data-pop-anchor]")) {
        closePopover();
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [popover]);

  /* ---------------- render helpers ---------------- */

  const renderLineRow = (line, index) => {
    const computed = lineTotals(line);
    return (
      <tr
        key={line.key}
        className={line.key === activeKey ? "is-active" : ""}
        onMouseDown={() => selectLine(line.key)}
      >
        <td className="pos-idx">{index + 1}</td>
        <td>
          <div className="pos-desc">
            <span className="pos-desc__name">{line.name}</span>
            <span className="pos-desc__meta">
              {[line.barcode || line.code, line.unitName, line.notes].filter(Boolean).join(" · ") || "—"}
            </span>
          </div>
        </td>
        <td>
          <div className="pos-qty">
            <button type="button" onClick={() => stepQuantity(line, -1)} aria-label="Less">
              <Minus />
            </button>
            <input
              type="number"
              min="0"
              step="any"
              value={line.quantity}
              onChange={(event) => updateLine(line.key, { quantity: event.target.value })}
              onFocus={() => selectLine(line.key)}
            />
            <button type="button" onClick={() => stepQuantity(line, 1)} aria-label="More">
              <Plus />
            </button>
          </div>
        </td>
        <td>
          <input
            className="pos-cellinput"
            type="number"
            min="0"
            step="any"
            value={line.price}
            onChange={(event) => updateLine(line.key, { price: event.target.value })}
            onFocus={() => selectLine(line.key)}
          />
        </td>
        <td>
          <input
            className="pos-cellinput"
            type="number"
            min="0"
            max="100"
            step="any"
            value={line.discountPercent}
            onChange={(event) => updateLine(line.key, { discountPercent: event.target.value })}
            onFocus={() => selectLine(line.key)}
          />
        </td>
        {showTax ? (
          <td>
            <select
              className="pos-cellinput"
              value={line.taxGuid ?? ""}
              onChange={(event) => {
                const guid = event.target.value || null;
                updateLine(line.key, { taxGuid: guid, taxPercent: taxOf(guid) });
              }}
            >
              <option value="">0.00</option>
              {(taxList || []).map((tax) => (
                <option key={tax.guid} value={tax.guid}>
                  {tax.percentage}
                </option>
              ))}
            </select>
          </td>
        ) : null}
        <td className="pos-num pos-net">{fmt(computed.net)}</td>
        <td className="pos-mid">
          <button type="button" className="pos-del" onClick={() => removeLine(line.key)} aria-label="Remove">
            <Trash2 />
          </button>
        </td>
      </tr>
    );
  };

  const paidTileClass = [
    "pos-paidtile",
    keypadTarget === "paid" ? "is-target" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="pos-screen">
      {/* ================= top bar ================= */}
      <div className="pos-topbar">
        <div className="pos-brand">
          <span className="pos-brand__icon">
            <ShoppingCart />
          </span>
          <h1 className="pos-brand__name">POS Billing</h1>
        </div>
        <span className={`pos-badge ${online ? "" : "pos-badge--offline"}`.trim()}>
          <span className="pos-badge__dot" />
          {online ? "Online" : "Offline"}
        </span>

        <div className="pos-topmeta">
          <div className="pos-topmeta__item">
            <span className="pos-topmeta__label">Date</span>
            <span className="pos-topmeta__value">{clock.format("DD/MM/YYYY, hh:mm A")}</span>
          </div>
          <div className="pos-topmeta__item">
            <span className="pos-topmeta__label">Transaction Date</span>
            <span className="pos-topmeta__value">
              <DatePicker
                value={invDate}
                allowClear={false}
                format="DD/MM/YY"
                suffixIcon={null}
                onChange={(date) => date && setInvDate(date)}
              />
            </span>
          </div>
          <div className="pos-topmeta__item">
            <span className="pos-topmeta__label">Store</span>
            <span className="pos-topmeta__value">
              {companyInfolist?.[0]?.name || "Store"} — {branch?.name || "Branch"}
            </span>
          </div>
          <div className="pos-topmeta__item">
            <span className="pos-topmeta__label">Shift</span>
            <span className="pos-topmeta__value">
              <Sun style={{ width: 14, height: 14, marginRight: 5 }} />
              {shiftLabel(clock)}
            </span>
          </div>
          <div className="pos-topmeta__item">
            <span className="pos-topmeta__label">Cashier</span>
            <span className="pos-topmeta__value">
              <User style={{ width: 14, height: 14, marginRight: 5 }} />
              {userName || userId?.name || "—"}
            </span>
          </div>
        </div>

        <div className="pos-topbar__actions">
          <button type="button" className="pos-topbtn" onClick={() => setDialog("keys")}>
            <HelpCircle />
            Shortcuts
          </button>
          <span className="pos-topbar__sep" />
          <button type="button" className="pos-topbtn" data-pop-anchor onClick={openPopover("settings")}>
            <Settings />
            Settings
          </button>
          <span className="pos-topbar__sep" />
          <button type="button" className="pos-topbtn" onClick={() => onExit("close")}>
            <LogOut />
            Exit
          </button>
        </div>
      </div>

      {/* ================= body ================= */}
      <div className="pos-main">
        {/* ---------------- left : the bill ---------------- */}
        <div className="pos-left">
          <div className="pos-card">
            <div className="pos-custrow">
              <POSField
                icon={<User />}
                label="Customer"
                action={
                  <button
                    type="button"
                    className="pos-field__add"
                    onClick={() => setCustomerModal(true)}
                    aria-label="Add customer"
                  >
                    <Plus />
                  </button>
                }
              >
                <Select
                  className="pos-select"
                  classNamePrefix="pos-select"
                  options={customerOptions}
                  value={customer}
                  isClearable
                  placeholder="Walk-in Customer"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  onChange={(option) => {
                    setCustomer(option);
                    setMobile(option?.customer?.mobile || "");
                  }}
                />
              </POSField>

              <POSField icon={<Phone />} label="Mobile">
                <input
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  placeholder="Mobile number"
                />
              </POSField>

              <POSField icon={<Home />} label="Warehouse">
                <Select
                  className="pos-select"
                  classNamePrefix="pos-select"
                  options={warehouseOptions}
                  value={warehouse}
                  placeholder="Select warehouse"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  onChange={setWarehouse}
                />
              </POSField>

              <POSField icon={<UserCheck />} label="Salesman">
                <Select
                  className="pos-select"
                  classNamePrefix="pos-select"
                  options={salesmanOptions}
                  value={salesman}
                  isClearable
                  placeholder="Select salesman"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  onChange={setSalesman}
                />
              </POSField>

              <button
                type="button"
                className={`pos-more ${showMore ? "is-on" : ""}`.trim()}
                onClick={() => setShowMore((prev) => !prev)}
                aria-label="More bill details"
              >
                <MoreVertical />
              </button>
            </div>

            {showMore ? (
              <div className="pos-extrarow">
                <POSField icon={<Hash />} label="Reference no.">
                  <input value={refNo} onChange={(event) => setRefNo(event.target.value)} placeholder="Reference" />
                </POSField>
                <POSField icon={<Truck />} label="Dispatch to">
                  <input
                    value={dispatchTo}
                    onChange={(event) => setDispatchTo(event.target.value)}
                    placeholder="Dispatch to"
                  />
                </POSField>
                <POSField icon={<FileText />} label="Terms">
                  <input value={terms} onChange={(event) => setTerms(event.target.value)} placeholder="Terms" />
                </POSField>
                <POSField icon={<Clock />} label="Delivery date">
                  <DatePicker
                    className="pos-input"
                    value={deliveryDate}
                    allowClear={false}
                    format="DD-MM-YYYY"
                    onChange={(date) => date && setDeliveryDate(date)}
                    style={{ border: 0, boxShadow: "none", padding: 0, width: "100%" }}
                  />
                </POSField>
              </div>
            ) : null}
          </div>

          {/* ---------------- scan ---------------- */}
          <div className="pos-scan">
            <span className="pos-scan__dot" />
            <input
              ref={scanRef}
              value={scan}
              onChange={(event) => setScan(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleScan(scan);
                } else if (event.key === "Escape") {
                  setScan("");
                }
              }}
              placeholder="Scan barcode, or type a name or code"
            />
            <span className="pos-scan__hint">
              <kbd>F3</kbd> search · <kbd>Esc</kbd> clear
            </span>
          </div>

          {/* ---------------- lines ---------------- */}
          <div className="pos-card pos-lines">
            <div className="pos-lines__head">
              <h5>Bill lines</h5>
              <span className="pos-count">
                {lines.length} items · {fmt(totals.units)} units
              </span>
              <div className="pos-lines__actions">
                <button type="button" className="pos-linkbtn" onClick={() => setDialog("manual")}>
                  <Plus />
                  Add manual item
                </button>
              </div>
            </div>

            <div className="pos-lines__body">
              <table className="pos-table">
                <colgroup>
                  <col className="c-idx" />
                  <col />
                  <col className="c-qty" />
                  <col className="c-price" />
                  <col className="c-disc" />
                  {showTax ? <col className="c-tax" /> : null}
                  <col className="c-net" />
                  <col className="c-act" />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th className="pos-mid">Qty</th>
                    <th className="pos-num">Price</th>
                    <th className="pos-num">Disc %</th>
                    {showTax ? <th className="pos-num">Tax %</th> : null}
                    <th className="pos-num">Net Amount</th>
                    <th className="pos-mid">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(renderLineRow)}

                  {/* the always-present picker row at the foot of the bill */}
                  <tr className="pos-row--new">
                    <td className="pos-idx">{lines.length + 1}</td>
                    <td colSpan={showTax ? 6 : 5}>
                      <Select
                        className="pos-select"
                        classNamePrefix="pos-select"
                        options={productOptions}
                        value={null}
                        placeholder="Select or search product…"
                        filterOption={productFilter}
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                        onChange={(option) => option && addProduct(option.product)}
                      />
                    </td>
                    <td className="pos-mid">
                      <button type="button" className="pos-del" disabled aria-label="Remove">
                        <Trash2 />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              {!lines.length ? (
                <p className="pos-empty">
                  Scan an item, or pick one from the row above, to start the bill.
                </p>
              ) : null}
            </div>
          </div>

          {/* ---------------- function tiles ---------------- */}
          <div className="pos-tiles">
            <button type="button" className="pos-tile" onClick={() => setDialog("manual")}>
              <span className="pos-tile__icon">
                <Tag />
              </span>
              <span className="pos-tile__label">Non Barcode Item</span>
            </button>
            <button type="button" className="pos-tile" onClick={() => setDialog("lookup")}>
              <span className="pos-tile__icon">
                <Search />
              </span>
              <span className="pos-tile__label">Item Look Up</span>
            </button>
            <button type="button" className="pos-tile" onClick={() => setDialog("quick")}>
              <span className="pos-tile__icon">
                <Grid />
              </span>
              <span className="pos-tile__label">Quick Item</span>
            </button>
            <button type="button" className="pos-tile" onClick={() => setDialog("stock")}>
              <span className="pos-tile__icon">
                <Package />
              </span>
              <span className="pos-tile__label">Stock & Price Check</span>
            </button>

            <button
              type="button"
              className="pos-tile pos-tile--value pos-tile--charge"
              data-pop-anchor
              onClick={openPopover("charge")}
            >
              <span className="pos-tile__icon">
                <Plus />
              </span>
              <span className="pos-tile__label">Extra Charge</span>
              <span className="pos-tile__value">{fmt(extraCharge)}</span>
            </button>
            <button
              type="button"
              className="pos-tile pos-tile--value pos-tile--discount"
              data-pop-anchor
              onClick={openPopover("discount")}
            >
              <span className="pos-tile__icon">
                <Percent />
              </span>
              <span className="pos-tile__label">Discount</span>
              <span className="pos-tile__value">{fmt(billDiscount)}</span>
            </button>
            <button
              type="button"
              className="pos-tile pos-tile--value pos-tile--round"
              data-pop-anchor
              onClick={openPopover("round")}
            >
              <span className="pos-tile__icon">
                <Circle />
              </span>
              <span className="pos-tile__label">Round Off</span>
              <span className="pos-tile__value">{fmt(-roundOff)}</span>
            </button>
          </div>

          {/* ---------------- bottom bar ---------------- */}
          <div className="pos-card pos-bottombar">
            <button type="button" className="pos-bbtn" onClick={() => onExit("salesReturn")}>
              <span className="pos-bbtn__icon">
                <CornerUpLeft />
              </span>
              <span>
                Sales Return
                <span className="pos-bbtn__key">Open screen</span>
              </span>
            </button>
            <button type="button" className="pos-bbtn" onClick={holdBill}>
              <span className="pos-bbtn__icon">
                <Pause />
              </span>
              <span>
                Hold Bill
                <span className="pos-bbtn__key">F7</span>
              </span>
            </button>
            <button type="button" className="pos-bbtn" onClick={() => setDialog("held")}>
              <span className="pos-bbtn__icon">
                <RotateCcw />
              </span>
              <span>
                Recall Bill
                <span className="pos-bbtn__key">F8 · {heldBills.length} held</span>
              </span>
            </button>
            <button type="button" className="pos-bbtn" onClick={openRecentBills}>
              <span className="pos-bbtn__icon">
                <Clock />
              </span>
              <span>
                Recent Bills
                <span className="pos-bbtn__key">Reprint</span>
              </span>
            </button>
            <button type="button" className="pos-bbtn" onClick={() => setDialog("notes")}>
              <span className="pos-bbtn__icon">
                <FileText />
              </span>
              <span>
                Notes
                <span className="pos-bbtn__key">{notes ? "1 note" : "None"}</span>
              </span>
            </button>
            <button type="button" className="pos-bbtn pos-bbtn--danger" onClick={confirmReset}>
              <span className="pos-bbtn__icon">
                <RotateCcw />
              </span>
              <span>
                Reset
                <span className="pos-bbtn__key">Clear bill</span>
              </span>
            </button>
          </div>
        </div>

        {/* ---------------- right : payment rail ---------------- */}
        <div className="pos-right">
          <div className="pos-rail">
            <div className="pos-card">
              <div className="pos-bill__head">
                <h5>Current bill</h5>
                <span className="pos-bill__no">#{entryNumber || "—"}</span>
              </div>

              <div className="pos-sum">
                <div className="pos-sum__row">
                  <span className="pos-sum__label">Subtotal</span>
                  <span className="pos-sum__value">{fmt(totals.gross)}</span>
                </div>
                <div className="pos-sum__row">
                  <span className="pos-sum__label">Line discounts</span>
                  <span className="pos-sum__value pos-sum__value--minus">{fmt(totals.lineDiscount)}</span>
                </div>
                {showTax ? (
                  <div className="pos-sum__row">
                    <span className="pos-sum__label">{taxLabel}</span>
                    <span className="pos-sum__value">{fmt(totals.tax)}</span>
                  </div>
                ) : null}
                {num(extraCharge) ? (
                  <div className="pos-sum__row">
                    <span className="pos-sum__label">Extra charge</span>
                    <span className="pos-sum__value">{fmt(extraCharge)}</span>
                  </div>
                ) : null}
                {num(billDiscount) ? (
                  <div className="pos-sum__row">
                    <span className="pos-sum__label">Bill discount</span>
                    <span className="pos-sum__value pos-sum__value--minus">−{fmt(billDiscount)}</span>
                  </div>
                ) : null}
                <div className="pos-sum__row">
                  <span className="pos-sum__label">Round off</span>
                  <span className="pos-sum__value">{fmt(-roundOff)}</span>
                </div>
              </div>

              <div className="pos-total">
                <span className="pos-total__label">Total due</span>
                <span className="pos-total__value">{fmt(totals.net)}</span>
              </div>
            </div>

            {/* ---------------- keypad ---------------- */}
            <div className="pos-card">
              <div className="pos-keyzone">
                {/* a div, not a button: it holds the editable paid input */}
                <div className={paidTileClass} onClick={() => switchTarget("paid")} role="presentation">
                  <span className="pos-paidtile__label">
                    <DollarSign />
                    Paid
                  </span>
                  <input
                    ref={paidRef}
                    className="pos-paidtile__value"
                    type="number"
                    min="0"
                    step="any"
                    value={isCreditOnly ? fmt(totals.net) : splitPay ? fmt(splitTotal) : paid}
                    disabled={splitPay || isCreditOnly}
                    placeholder="0.00"
                    onFocus={() => switchTarget("paid")}
                    onChange={(event) => {
                      setPaid(event.target.value);
                      setEntry(event.target.value);
                    }}
                  />
                </div>

                <div
                  className={`pos-paidtile ${change >= 0 ? "pos-paidtile--change" : "pos-paidtile--short"}`}
                >
                  <span className="pos-paidtile__label">{change >= 0 ? "Change" : "Short by"}</span>
                  <span className="pos-paidtile__value">{fmt(Math.abs(change))}</span>
                </div>

                {["7", "8", "9"].map((token) => (
                  <button key={token} type="button" className="pos-key" onClick={() => pressKey(token)}>
                    {token}
                  </button>
                ))}
                <button type="button" className="pos-key pos-key--fn" onClick={() => pressKey("back")}>
                  <Delete />
                </button>

                {["4", "5", "6"].map((token) => (
                  <button key={token} type="button" className="pos-key" onClick={() => pressKey(token)}>
                    {token}
                  </button>
                ))}
                <button type="button" className="pos-key pos-key--fn" onClick={() => pressKey("+")}>
                  <Plus />
                </button>

                {["1", "2", "3"].map((token) => (
                  <button key={token} type="button" className="pos-key" onClick={() => pressKey(token)}>
                    {token}
                  </button>
                ))}
                <button type="button" className="pos-key pos-key--fn" onClick={() => pressKey("-")}>
                  <Minus />
                </button>

                {["00", "0", "."].map((token) => (
                  <button key={token} type="button" className="pos-key" onClick={() => pressKey(token)}>
                    {token}
                  </button>
                ))}
                <button type="button" className="pos-key pos-key--fn" onClick={() => pressKey("clear")}>
                  C
                </button>

                <button
                  type="button"
                  className={`pos-key pos-key--apply pos-key--span2 ${
                    keypadTarget === "qty" ? "is-on" : ""
                  }`.trim()}
                  disabled={!activeLine}
                  onClick={() => switchTarget("qty")}
                >
                  Update
                  <strong>QUANTITY</strong>
                </button>
                <button
                  type="button"
                  className={`pos-key pos-key--apply pos-key--span2 ${
                    keypadTarget === "price" ? "is-on" : ""
                  }`.trim()}
                  disabled={!activeLine}
                  onClick={() => switchTarget("price")}
                >
                  Update
                  <strong>PRICE</strong>
                </button>
              </div>

              <div className="pos-quickcash">
                <span className="pos-quickcash__label">Quick Cash</span>
                {quickCash.length ? (
                  quickCash.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`pos-cashchip ${num(paid) === value ? "is-on" : ""}`.trim()}
                      disabled={splitPay || isCreditOnly}
                      onClick={() => {
                        switchTarget("paid");
                        setPaid(`${value}`);
                        setEntry(`${value}`);
                      }}
                    >
                      {value}
                    </button>
                  ))
                ) : (
                  <span className="pos-quickcash__label">Add items to see tender suggestions</span>
                )}
              </div>
            </div>

            {/* ---------------- payment & checkout ---------------- */}
            <div className="pos-card">
              <div className="pos-pay__head">
                <span className="pos-pay__icon">
                  <CreditCard />
                </span>
                <h5>Payment &amp; checkout</h5>
                <button
                  type="button"
                  className={`pos-switch ${splitPay ? "is-on" : ""}`.trim()}
                  onClick={() => {
                    setSplitPay((prev) => !prev);
                    setPayments(ZERO_PAYMENTS);
                    setPaid("");
                  }}
                >
                  Split Payment
                  <span className="pos-switch__track" />
                </button>
              </div>

              <div className="pos-paygrid">
                {PAY_MODES.map((mode) => (
                  <div
                    key={mode.key}
                    role="presentation"
                    className={`pos-paymode ${mode.className} ${
                      !splitPay && payMode === mode.value ? "is-on" : ""
                    }`.trim()}
                    // whole tile picks the mode; in split mode each tile just
                    // holds its own amount, so the click does nothing
                    onClick={() => !splitPay && setPayMode(mode.value)}
                    style={{ cursor: splitPay ? "default" : "pointer" }}
                  >
                    <span className="pos-paymode__icon">{mode.icon}</span>
                    <span className="pos-paymode__name">{mode.label}</span>
                    {splitPay ? (
                      <>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={payments[mode.key]}
                          placeholder="0.00"
                          onChange={(event) =>
                            setPayments((prev) => ({ ...prev, [mode.key]: event.target.value }))
                          }
                        />
                        <button
                          type="button"
                          className="pos-paymode__clear"
                          onClick={() => setPayments((prev) => ({ ...prev, [mode.key]: "" }))}
                          aria-label={`Clear ${mode.label}`}
                        >
                          <X />
                        </button>
                      </>
                    ) : (
                      <span style={{ marginLeft: "auto", fontWeight: 700 }}>
                        {payMode === mode.value ? fmt(totals.net) : "—"}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div className={`pos-paynote ${paidTotal < totals.net - 0.005 ? "pos-paynote--warn" : ""}`.trim()}>
                <span>{splitPay ? "Split total" : `Tendered in ${PAY_MODES.find((m) => m.value === payMode)?.label}`}</span>
                <strong>
                  {fmt(paidTotal)} / {fmt(totals.net)}
                </strong>
              </div>
            </div>
          </div>

          <div className="pos-save">
            <button type="button" className="pos-savebtn" disabled={saving} onClick={() => saveBill(false)}>
              <span className="pos-savebtn__icon">
                <Save />
              </span>
              <span className="pos-savebtn__text">
                SAVE
                <small>F9</small>
              </span>
            </button>
            <button
              type="button"
              className="pos-savebtn pos-savebtn--print"
              disabled={saving}
              onClick={() => saveBill(true)}
            >
              <span className="pos-savebtn__icon">
                <Printer />
              </span>
              <span className="pos-savebtn__text">
                SAVE &amp; PRINT
                <small>F10</small>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ================= popovers ================= */}
      {popover?.name === "charge" ? (
        <div className="pos-pop" style={{ left: popover.left, top: popover.top, transform: "translateY(-100%)" }}>
          <h6>Extra charge</h6>
          <div className="pos-pop__row">
            <input
              autoFocus
              type="number"
              min="0"
              step="any"
              value={extraCharge}
              onChange={(event) => setExtraCharge(num(event.target.value))}
            />
            <button type="button" className="pos-btn" onClick={closePopover}>
              Done
            </button>
          </div>
          <p className="pos-hint">Delivery, packing or any other charge added to this bill.</p>
        </div>
      ) : null}

      {popover?.name === "discount" ? (
        <div className="pos-pop" style={{ left: popover.left, top: popover.top, transform: "translateY(-100%)" }}>
          <h6>Bill discount</h6>
          <div className="pos-pop__row">
            <input
              autoFocus
              type="number"
              min="0"
              step="any"
              value={billDiscount}
              onChange={(event) => setBillDiscount(num(event.target.value))}
            />
            <button type="button" className="pos-btn" onClick={closePopover}>
              Done
            </button>
          </div>
          <div className="pos-pop__seg" style={{ marginTop: 8 }}>
            {[5, 10, 15].map((percent) => (
              <button
                key={percent}
                type="button"
                onClick={() => setBillDiscount(Number(((totals.net + billDiscount) * percent) / 100))}
              >
                {percent}%
              </button>
            ))}
            <button type="button" onClick={() => setBillDiscount(0)}>
              Clear
            </button>
          </div>
          <p className="pos-hint">Taken off the bill after line discounts and tax.</p>
        </div>
      ) : null}

      {popover?.name === "round" ? (
        <div className="pos-pop" style={{ left: popover.left, top: popover.top, transform: "translateY(-100%)" }}>
          <h6>Round off</h6>
          <div className="pos-pop__row">
            <input
              autoFocus
              type="number"
              step="any"
              value={roundOff}
              onChange={(event) => setRoundOff(num(event.target.value))}
            />
            <button
              type="button"
              className="pos-btn"
              onClick={() => {
                // The figure that lands the total on a whole unit.
                const before = totals.net + roundOff;
                setRoundOff(Number((before - Math.round(before)).toFixed(decimals)));
              }}
            >
              Auto
            </button>
          </div>
          <p className="pos-hint">A positive figure reduces the total due.</p>
        </div>
      ) : null}

      {popover?.name === "settings" ? (
        <div className="pos-pop" style={{ left: Math.min(popover.left, window.innerWidth - 290), top: 62 }}>
          <h6>Terminal settings</h6>
          <label className="pos-pop__check">
            <input type="checkbox" checked={printOnSave} onChange={(event) => setPrintOnSave(event.target.checked)} />
            Print a receipt on save
          </label>
          <label className="pos-pop__check">
            <input type="checkbox" checked={showMore} onChange={(event) => setShowMore(event.target.checked)} />
            Show reference, terms and delivery
          </label>
          <p className="pos-hint">
            Tax columns follow the branch setting, and amounts use {decimals} decimals from the currency format.
          </p>
        </div>
      ) : null}

      {/* ================= dialogs ================= */}
      {dialog === "lookup" || dialog === "quick" ? (
        <ItemLookupDialog
          products={productList}
          decimals={decimals}
          quick={dialog === "quick"}
          initialTerm={dialog === "lookup" ? lookupTerm : ""}
          onPick={(product) => {
            addProduct(product);
            if (dialog === "lookup") {
              setDialog(null);
              setLookupTerm("");
              focusScan();
            }
          }}
          onClose={() => {
            setDialog(null);
            setLookupTerm("");
            focusScan();
          }}
        />
      ) : null}

      {dialog === "stock" ? (
        <StockPriceCheckDialog
          products={productList}
          taxOf={taxOf}
          decimals={decimals}
          onAdd={(product) => {
            addProduct(product);
            setDialog(null);
            focusScan();
          }}
          onClose={() => {
            setDialog(null);
            focusScan();
          }}
        />
      ) : null}

      {dialog === "manual" ? (
        <ManualItemDialog
          products={productOptions}
          taxOptions={taxList || []}
          onSave={(item) => {
            const line = { ...emptyLine(), ...item, manual: true };
            setLines((prev) => [...prev, line]);
            setActiveKey(line.key);
            setDialog(null);
            focusScan();
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === "recent" ? (
        <RecentBillsDialog
          bills={recentBills}
          loading={recentLoading}
          decimals={decimals}
          onPrint={printSavedBill}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === "held" ? (
        <HeldBillsDialog
          bills={heldBills}
          decimals={decimals}
          onRecall={recallBill}
          onDelete={deleteHeldBill}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === "notes" ? (
        <NotesDialog
          value={notes}
          onSave={(text) => {
            setNotes(text);
            setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === "keys" ? <ShortcutsDialog onClose={() => setDialog(null)} /> : null}

      {customerModal ? (
        <CustomerModal
          mode="add"
          data={{}}
          handleClose={() => setCustomerModal(false)}
          handleRefresh={() => dispatch(fetchCustomer(branch))}
        />
      ) : null}
    </div>
  );
};

POSBillingScreen.propTypes = {
  onExit: PropTypes.func.isRequired,
};

export default POSBillingScreen;
